import { Gift } from 'lucide-preact';
import { useState } from 'preact/hooks';

import { useWidgetConfig } from '@/context/WidgetConfigContext';
import {
  FLOATING_WIDGET_TRIGGER_DEFAULT_OFFSET,
  FLOATING_WIDGET_TRIGGER_DEFAULT_OFFSET_MOBILE,
  FLOATING_WIDGET_TRIGGER_LIFT_OFFSET,
} from '@/helpers/constants/layout';
import { COLORS } from '@/helpers/constants/theme';
import { useHoverBackground } from '@/hooks/useHoverBackground';
import { useIsMobile } from '@/hooks/useIsMobile';
import {
  E_FloatingWidgetMobileMessageLocation,
  E_FloatingWidgetTriggerLocation,
} from '@/types/FloatingWidgetTrigger';
import type { I_BaseWidgetProps } from '@/types/Widget';
import { cn } from '@/ui/utils';

import { MessageBubble } from './MessageBubble';

export const FloatingWidgetTrigger = ({ isWidgetOpen, openWidget }: I_BaseWidgetProps) => {
  const [showMessage, setShowMessage] = useState(true);

  const { widgetLocation, messageLocation, mobileMessageLocation, theme, showMessages } =
    useWidgetConfig();

  const {
    backgroundColor = COLORS.BRAND_PRIMARY,
    textColor = COLORS.TEXT_INVERSE,
    iconColor = COLORS.TEXT_INVERSE,
    label = 'Rewards',
  } = theme?.floatingButton ?? {};

  const {
    backgroundColor: hoverBackground,
    hoverHandlers,
    isHovered,
  } = useHoverBackground({
    baseColor: backgroundColor,
    onEnter: () => {
      if (!isWidgetOpen) setShowMessage(true);
    },
  });

  const isMobile = useIsMobile();

  const effectiveMessageLocation = isMobile ? mobileMessageLocation : messageLocation;
  const shouldLiftTrigger =
    !isWidgetOpen &&
    showMessages &&
    showMessage &&
    effectiveMessageLocation === E_FloatingWidgetMobileMessageLocation.BELOW;

  const containerClasses = cn(
    'fixed z-50 transition-all duration-300',
    widgetLocation === E_FloatingWidgetTriggerLocation.BOTTOM_LEFT ? 'left-6' : 'right-6',
    shouldLiftTrigger
      ? FLOATING_WIDGET_TRIGGER_LIFT_OFFSET
      : isMobile
        ? FLOATING_WIDGET_TRIGGER_DEFAULT_OFFSET_MOBILE
        : FLOATING_WIDGET_TRIGGER_DEFAULT_OFFSET,
  );

  return (
    <div className={containerClasses}>
      <div className='relative'>
        {showMessages && (
          <MessageBubble
            isWidgetOpen={isWidgetOpen}
            openWidget={openWidget}
            showMessage={showMessage}
            setShowMessage={setShowMessage}
            isHovered={isHovered}
          />
        )}

        <div
          id='floating-widget-trigger'
          onClick={openWidget}
          {...hoverHandlers}
          className={cn(
            'relative bottom-[3px] rounded-full p-4',
            'cursor-pointer shadow-lg transition-all duration-300',
            'group hover:scale-110',
          )}
          style={{
            backgroundColor: hoverBackground,
            color: textColor,
          }}
          aria-label={isWidgetOpen ? 'Close loyalty widget' : 'Open loyalty widget'}
          aria-expanded={isWidgetOpen}
          aria-controls='floating-widget-body'
        >
          <div className='flex items-center space-x-2'>
            <Gift className='h-6 w-6' style={{ color: iconColor }} />
            {label.trim() && <h5>{label}</h5>}
          </div>
        </div>
      </div>
    </div>
  );
};
